/* Live updates of the Android app's web layer (see GrowDailyUpdaterPlugin.java
 * and scripts/make-app-bundle.cjs). A release of growdaily.fun reaches the app
 * without a Play Store upload: the native side downloads the newer bundle and
 * switches to it on the next start, or right away when the user taps Restart.
 * Does nothing in a browser or in an app build without the updater.
 */
const MANIFEST = 'https://growdaily.fun/app-update/manifest.json';
const MIN_GAP_MS = 3 * 60 * 60 * 1000;
let lastCheck = 0;
let runningVersionRequest = null;
let checkInFlight = null;

export function updaterBridge() {
    if (typeof window === 'undefined' || !window.Capacitor?.isNativePlatform?.()) return null;
    return window.Capacitor?.Plugins?.GrowDailyUpdater || null;
}

async function runningVersion() {
    // Retain this page's identity after an update is staged; a resumed old
    // page must not acknowledge the new bundle on its behalf.
    if (!runningVersionRequest) {
        runningVersionRequest = (async () => {
            try {
                const r = await fetch('/app-version.json', { cache: 'no-store' });
                const value = r.ok ? await r.json() : null;
                if (!/^[a-f0-9]{8,64}$/.test(value?.version || '')
                    || !/^[a-f0-9]{8,64}$/.test(value?.edenHash || '')
                    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value?.builtAt || '')
                    || new Date(value.builtAt).toISOString() !== value.builtAt) return null;
                return value;
            } catch { return null; }
        })();
    }
    return runningVersionRequest;
}

/** Resolves with the native status ('current', 'installed', 'metered', ...) or null. */
export async function checkForAppUpdate({ force = false } = {}) {
    const bridge = updaterBridge();
    if (!bridge) return null;
    if (checkInFlight) return checkInFlight;
    checkInFlight = (async () => {
        const self = await runningVersion();
        if (!self) return null;
        try { await bridge.markHealthy({ current: self.version }); } catch { /* older native build */ }
        if (!force && Date.now() - lastCheck < MIN_GAP_MS) return null;
        lastCheck = Date.now();
        try {
            const r = await bridge.check({ manifest: MANIFEST, current: self.version, builtAt: self.builtAt, edenHash: self.edenHash });
            return r?.status || null;
        } catch { return null; }
    })();
    try {
        return await checkInFlight;
    } finally { checkInFlight = null; }
}

export async function applyAppUpdateNow() {
    const bridge = updaterBridge();
    if (!bridge) return false;
    try { await bridge.applyNow(); return true; } catch { return false; }
}
